import Container from '@/components/Container';
import DefaultContentTemplate from '@/components/DefaultContentTemplate';
import DefaultContentTemplateMedia from '@/components/DefaultContentTemplate/Media';
import { RelatedContent } from '@/components/DefaultContentTemplate/RelatedContent';
import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import Divider from '@/components/Divider';
import {
  Enum_Favorite_Type,
  KnowledgeHubPage,
  MeditationPageQuery,
  MeditationPageQueryVariables,
  ResourceEntity,
  UploadFile,
} from '@/graphql/generated/graphql';
import useTranslation from '@/hooks/i18n';
import { fetchRelatedContent, fetchResource } from '@/services/knowledge-hub/resource.service';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/ui/breadcrumb';
import TitleWithActions from '@/components/TitleWithActions';
import LikeAndFavorite from '@/components/LikeAndFavorite';
import { fetchKnowledgeHubPage } from '@/services/knowledge-hub/knowledge-hub.service';
import { clearResourceDataLayer, trackResource } from '@/lib/ga-custom-events/resource';
import { QUERY_MEDITATION_PAGE } from '@/graphql/queries/meditation/meditation';
import { useQuery } from '@apollo/client';

export default function ResourcePage() {
  const [resource, setResource] = useState<ResourceEntity | null>(null);
  const [relatedContent, setRelatedContent] = useState<RelatedContent[] | null>(null);
  const [knowledgeHubPage, setKnowledgeHubPage] = useState<KnowledgeHubPage | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const { slug } = useParams();
  const { t } = useTranslation();

  const { data: pageData } = useQuery<MeditationPageQuery, MeditationPageQueryVariables>(QUERY_MEDITATION_PAGE);

  useEffect(() => {
    (async () => {
      if (!slug) return;
      setLoading(true);
      const [resourceResult, relatedContentResult, page] = await Promise.all([
        fetchResource(slug),
        fetchRelatedContent(slug),
        fetchKnowledgeHubPage(),
      ]);

      const resourceData = resourceResult?.resource;
      setKnowledgeHubPage(page.page);
      setResource(resourceData);
      setRelatedContent(relatedContentResult.resources || []);
      setLoading(false);

      if (resourceData) {
        const topic = resourceData.attributes?.themes?.data[0]?.attributes?.topic?.data?.attributes;
        const topicId = resourceData.attributes?.themes?.data[0]?.attributes?.topic?.data?.id;
        const themes = resourceData.attributes?.themes?.data?.map((theme) => `${theme.attributes?.theme}`).join(', ');
        const themeId = resourceData.attributes?.themes?.data?.map((theme) => `${theme.id}`).join(', ');

        trackResource({
          topic_id: topicId || '',
          topic_name: topic?.topic || '',
          theme_id: themeId || '',
          theme_name: themes || '',
        });
      }
    })();
  }, [slug]);

  useEffect(() => {
    return () => {
      clearResourceDataLayer();
    };
  }, []);

  const renderResourceHeader = () => {
    const topicNative = resource?.attributes?.themes?.data[0]?.attributes?.topic?.data?.attributes?.topic;
    const topic = t(topicNative || '');
    const themes =
      resource?.attributes?.themes?.data
        .map((theme) => t(theme?.attributes?.theme?.toString() || '') || '')
        .join(', ') || '';

    const items = [
      {
        label: <>knowledge hub</>,
        url: '/knowledge-hub',
      },
      {
        label: <>{topic}</>,
        url: `/knowledge-hub/topic/${topicNative}`,
      },
      { label: themes },
    ];

    return (
      <div>
        <Breadcrumb items={items} className="!my-0" />
        <Container>
          <div className="col-span-12 lg:col-start-3 lg:col-end-11">
            <DefaultContentTemplateMedia
              className="!mb-sh-15 mt-sh-30"
              component={{
                id: '1',
                media: {
                  data: {
                    attributes: {
                      url: resource?.attributes?.cover.data?.attributes?.url || '',
                      alternativeText: resource?.attributes?.cover.data?.attributes?.alternativeText || '',
                    } as UploadFile,
                  },
                },
              }}
            />

            {resource?.attributes?.duration && resource?.id && (
              <div className="!mb-sh-15 flex items-center gap-sh-15">
                <DefaultContentTemplateTags
                  className="!mb-0 !mt-0"
                  duration={resource?.attributes?.duration?.toString() || ''}
                  tags={[resource?.attributes?.resourceType?.data?.attributes?.type?.toString() || '']}
                />
                <LikeAndFavorite entity={resource} type={Enum_Favorite_Type.Resource} entityId={resource!.id} />
              </div>
            )}

            {resource?.id ? (
              <TitleWithActions
                title={resource!.attributes!.title}
                entity={resource}
                favorite={false}
                like={false}
                type={Enum_Favorite_Type.Resource}
                entityId={resource!.id}
              />
            ) : null}

            <p className="my-sh-15 text-sh-18-24">{resource?.attributes?.subHeader}</p>

            <Divider />
          </div>
        </Container>
      </div>
    );
  };

  return (
    <>
      {!loading && renderResourceHeader()}
      <Container className="min-h-40">
        <div className="col-span-12 lg:col-start-3 lg:col-end-11">
          <DefaultContentTemplate
            loading={loading}
            content={resource?.attributes?.DefaultContentTemplate}
            tags={resource?.attributes?.themes?.data.map((theme) => theme?.attributes?.theme?.toString() || '') || []}
            relatedContent={relatedContent || []}
            downloadableContentTitle={knowledgeHubPage?.contentTemplateLabels?.downloadableContentLabel || undefined}
            meditationCard={{
              image: pageData?.meditationPage?.data?.attributes?.header?.image?.data?.attributes?.url || '',
            }}
          />
        </div>
      </Container>
    </>
  );
}
